"use client";

import { createContext, useCallback, useContext, useMemo, useState } from "react";

import { useOpenWithFocus } from "./use-open-with-focus";
import { preloadSiteData } from "./site-data";

export type SearchSource = "header" | "dock";

type SearchContextValue = {
  open: boolean;
  source: SearchSource;
  setOpen: (open: boolean, source?: SearchSource) => void;
};

const SearchContext = createContext<SearchContextValue | null>(null);

export function SearchProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpenState] = useOpenWithFocus(false);
  const [source, setSource] = useState<SearchSource>("header");

  const setOpen = useCallback(
    (next: boolean, nextSource?: SearchSource) => {
      if (next) preloadSiteData();
      if (next && nextSource) setSource(nextSource);
      setOpenState(next);
    },
    [setOpenState],
  );

  const value = useMemo(() => ({ open, source, setOpen }), [open, source, setOpen]);
  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
}

export function useSearch() {
  const ctx = useContext(SearchContext);
  if (!ctx) throw new Error("useSearch must be used inside SearchProvider");
  return ctx;
}
